import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../common/components/xepp/button/Button";
import styles from "./DepartmentsPage.module.scss";
import { ApplicationRoutes } from "../../../routes";
import { useGetDepartmentsQuery, useDeleteDepartmentMutation } from "../../../service/department-service";
import ConfirmationModal from "../../../common/components/xepp/confirmation-modal/ConfirmationModal";
import { toast } from "react-toastify";
import Can from "../../../guards/Can";
import { UtilityPermissions } from "../../../model/permissions/permissions";

// Helper function to generate avatar color based on department name
const getAvatarColor = (name: string) => {
	const colors = ["#EF4444", "#EC4899", "#10B981", "#6B7280", "#3B82F6", "#F59E0B", "#8B5CF6", "#14B8A6"];
	const index = name.charCodeAt(0) % colors.length;
	return colors[index];
};

type Props = {};

const DepartmentsPage = memo((props: Props) => {
	const navigate = useNavigate();
	const { data: departments, isLoading, error, refetch } = useGetDepartmentsQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});
	const [deleteDepartment, { isLoading: isDeleting }] = useDeleteDepartmentMutation();
	const [openMenuId, setOpenMenuId] = useState<string | null>(null);
	const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; departmentId: string; departmentName: string }>({
		isOpen: false,
		departmentId: "",
		departmentName: "",
	});

	const handleCreateDepartment = () => {
		navigate(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}/${ApplicationRoutes.DEPARTMENT_CONFIGURATION}/${ApplicationRoutes.CREATE_DEPARTMENT}`);
	};

	const handleDepartmentClick = (departmentId: string) => {
		navigate(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}/${ApplicationRoutes.DEPARTMENT_CONFIGURATION}/${departmentId}`);
	};

	const handleMenuToggle = (event: React.MouseEvent, departmentId: string) => {
		event.stopPropagation();
		setOpenMenuId(prev => (prev === departmentId ? null : departmentId));
	};

	const handleDeleteClick = (event: React.MouseEvent, departmentId: string, departmentName: string) => {
		event.stopPropagation();
		setDeleteModal({
			isOpen: true,
			departmentId,
			departmentName,
		});
		setOpenMenuId(null);
	};

	const handleConfirmDelete = async () => {
		try {
			await deleteDepartment(deleteModal.departmentId).unwrap();
			toast.success("Department deleted successfully!");
			setDeleteModal({ isOpen: false, departmentId: "", departmentName: "" });
			refetch();
		} catch (error: any) {
			toast.error(error?.data?.message || "Failed to delete department. Please try again.");
		}
	};

	const handleCloseModal = () => {
		setDeleteModal({ isOpen: false, departmentId: "", departmentName: "" });
	};

	if (isLoading) {
		return (
			<div className={styles.container}>
				<div className={styles.header}>
					<div className={styles.title}>Departments</div>
				</div>
				<div style={{ textAlign: 'center', padding: '2rem' }}>Loading departments...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.container}>
				<div className={styles.header}>
					<div className={styles.title}>Departments</div>
				</div>
				<div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
					Error loading departments. Please try again.
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<div className={styles.title}>Departments</div>
				<Can permission={UtilityPermissions.CreateDepartment}>
					<Button
						text="Add New Department"
						variant="primary"
						onClick={handleCreateDepartment}
					/>
				</Can>
			</div>

			<div className={styles.departmentGrid}>
				{departments?.map((dept) => (
					<div
						key={dept.id}
						className={styles.departmentCard}
						onClick={() => handleDepartmentClick(dept.id)}
						style={{ cursor: 'pointer' }}
					>
						<div className={styles.cardHeader}>
							<div className={styles.departmentInfo}>
								<div
									className={styles.avatar}
									style={{ backgroundColor: getAvatarColor(dept.name) }}
								>
									{dept.name.charAt(0).toUpperCase()}
								</div>
								<h3 className={styles.departmentName}>{dept.name}</h3>
							</div>
							<Can permission={UtilityPermissions.DeleteDepartment}>
								<div className={styles.menuWrapper}>
									<button
										className={styles.menuButton}
										onClick={(event) => handleMenuToggle(event, dept.id)}
									>
										<span>⋮</span>
									</button>
									{openMenuId === dept.id && (
										<div className={styles.menuDropdown}>
											<button
												className={styles.menuItemDelete}
												onClick={(event) => handleDeleteClick(event, dept.id, dept.name)}
											>
												<span>🗑</span>
												<span>Delete department</span>
											</button>
										</div>
									)}
								</div>
							</Can>
						</div>
						<p className={styles.description}>{dept.description}</p>
						<div className={styles.peopleCount}>
							{dept.peopleCount} {dept.peopleCount === 1 ? 'Person' : 'People'}
						</div>
					</div>
				))}
			</div>
			<ConfirmationModal
				isOpen={deleteModal.isOpen}
				onClose={handleCloseModal}
				onConfirm={handleConfirmDelete}
				title="Delete Department"
				message={`Are you sure you want to delete "${deleteModal.departmentName}"? This action cannot be undone.`}
				confirmText="Delete"
				cancelText="Cancel"
				isLoading={isDeleting}
				variant="danger"
			/>
		</div>
	);
});

export default DepartmentsPage;
